import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./ServicosForm.css";

const API_URL = "https://acesso.cronosagenda.com.br/api";

const ServicosForm = ({ servicoParaEditar, onClose, onSave }) => {
  const [nome, setNome] = useState("");
  const [preco, setPreco] = useState("");
  const [duracao, setDuracao] = useState("");
  const [profissionais, setProfissionais] = useState([]);
  const [descricao, setDescricao] = useState("");
  const [profissionaisSelecionados, setProfissionaisSelecionados] = useState([]);
  const [precoPersonalizado, setPrecoPersonalizado] = useState("");
  const [usarPrecoPersonalizado, setUsarPrecoPersonalizado] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [loading, setLoading] = useState(false);
  const [carregandoProfissionais, setCarregandoProfissionais] = useState(true);
  const [isEdit, setIsEdit] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    let ativo = true;

    const carregarProfissionais = async () => {
      setCarregandoProfissionais(true);

      try {
        const response = await axios.get(`${API_URL}/funcionarios`, {
          withCredentials: true,
        });

        if (ativo) {
          setProfissionais(
            Array.isArray(response.data) ? response.data : []
          );
        }
      } catch (err) {
        console.error("Erro ao carregar profissionais:", err);

        if (ativo) {
          setErro(
            err.response?.data?.error ||
            "Não foi possível carregar a lista de profissionais."
          );
        }
      } finally {
        if (ativo) {
          setCarregandoProfissionais(false);
        }
      }
    };

    carregarProfissionais();

    if (servicoParaEditar) {
      setIsEdit(true);
      setNome(servicoParaEditar.nome || "");
      setPreco(
        servicoParaEditar.preco != null
          ? String(servicoParaEditar.preco)
          : ""
      );
      setDuracao(
        servicoParaEditar.duracao != null
          ? String(servicoParaEditar.duracao)
          : ""
      );
      setProfissionaisSelecionados(
        (servicoParaEditar.profissionais_ids || []).map(String)
      );
      setPrecoPersonalizado(servicoParaEditar.preco_personalizado || "");
      setUsarPrecoPersonalizado(
        Boolean(servicoParaEditar.preco_personalizado)
      );
      setDescricao(servicoParaEditar.descricao || "");
    } else {
      setIsEdit(false);
      setNome("");
      setPreco("");
      setDuracao("");
      setProfissionaisSelecionados([]);
      setPrecoPersonalizado("");
      setUsarPrecoPersonalizado(false);
      setDescricao("");
    }

    return () => {
      ativo = false;
    };
  }, [servicoParaEditar]);

  useEffect(() => {
    if (!sucesso) return undefined;

    const timer = setTimeout(() => {
      setSucesso("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [sucesso]);

  const handleProfissionalChange = (profissionalId) => {
    const idNormalizado = String(profissionalId);

    setProfissionaisSelecionados((prev) => {
      if (prev.includes(idNormalizado)) {
        return prev.filter((idSelecionado) => idSelecionado !== idNormalizado);
      }

      return [...prev, idNormalizado];
    });
  };

  const handlePrecoPersonalizadoToggle = (usar) => {
    setUsarPrecoPersonalizado(usar);

    if (!usar) {
      setPrecoPersonalizado("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");

    if (!nome.trim() || !duracao) {
      setErro("Nome e duração são obrigatórios.");
      return;
    }

    if (Number(duracao) <= 0 || !Number.isFinite(Number(duracao))) {
      setErro("Informe uma duração válida, maior que zero.");
      return;
    }

    if (!usarPrecoPersonalizado) {
      if (preco === "" || !Number.isFinite(Number(preco)) || Number(preco) < 0) {
        setErro("Informe um preço válido, igual ou maior que zero.");
        return;
      }
    }

    if (usarPrecoPersonalizado && !precoPersonalizado.trim()) {
      setErro("Informe o texto do preço personalizado.");
      return;
    }

    setLoading(true);

    try {
      await axios.get(`${API_URL}/verificar-autenticacao`, {
        withCredentials: true,
      });

      const serviceData = {
        nome: nome.trim(),
        preco: usarPrecoPersonalizado ? "0" : preco,
        duracao,
        profissionais_ids: profissionaisSelecionados,
        preco_personalizado: usarPrecoPersonalizado
          ? precoPersonalizado.trim()
          : null,
        descricao: descricao.trim() || null,
      };

      if (isEdit) {
        await axios.put(
          `${API_URL}/servicos/${servicoParaEditar.id}`,
          serviceData,
          { withCredentials: true }
        );

        setSucesso("Serviço editado com sucesso!");

        if (onSave) {
          onSave("Serviço editado com sucesso!");
        }
      } else {
        await axios.post(
          `${API_URL}/servicos`,
          serviceData,
          { withCredentials: true }
        );

        setSucesso("Serviço criado com sucesso!");

        if (onSave) {
          onSave("Serviço criado com sucesso!");
        }

        navigate("/dashboard");
      }

      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error("Erro ao salvar o serviço:", err);

      if (err.response?.status === 401) {
        setErro("Sua sessão expirou. Faça login novamente.");
        navigate("/login");
      } else {
        setErro(
          err.response?.data?.error ||
          err.response?.data?.erro ||
          err.response?.data?.mensagem ||
          "Não foi possível salvar o serviço. Verifique os dados e tente novamente."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="servicos-form-page">
      <section className="servicos-form-card">
        <header className="servicos-form-header">
          <div className="servicos-form-header-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M12 3v18M3 12h18" />
              <rect x="4" y="4" width="16" height="16" rx="4" />
            </svg>
          </div>

          <div className="servicos-form-heading">
            <span className="servicos-form-eyebrow">GESTÃO DE SERVIÇOS</span>
            <h1>{isEdit ? "Editar serviço" : "Novo serviço"}</h1>
            <p>
              {isEdit
                ? "Atualize os detalhes e as opções deste serviço."
                : "Cadastre um serviço para disponibilizá-lo na sua agenda."}
            </p>
          </div>
        </header>

        {erro && (
          <div className="servicos-form-alert servicos-form-alert-error" role="alert">
            <span className="servicos-form-alert-symbol">!</span>
            <p>{erro}</p>
            <button
              type="button"
              className="servicos-form-alert-close"
              onClick={() => setErro("")}
              aria-label="Fechar mensagem"
            >
              ×
            </button>
          </div>
        )}

        {sucesso && (
          <div className="servicos-form-toast" role="status">
            <span className="servicos-form-toast-check">✓</span>
            <span>{sucesso}</span>
            <button
              type="button"
              onClick={() => setSucesso("")}
              aria-label="Fechar mensagem"
            >
              ×
            </button>
          </div>
        )}

        <form className="servicos-form" onSubmit={handleSubmit}>
          <section className="servicos-form-section">
            <div className="servicos-form-section-heading">
              <span className="servicos-form-section-number">01</span>
              <div>
                <h2>Informações principais</h2>
                <p>Defina o nome, o preço e a duração do serviço.</p>
              </div>
            </div>

            <div className="servicos-form-field">
              <label htmlFor="servico-nome">Nome do serviço *</label>
              <input
                id="servico-nome"
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex.: Corte de cabelo"
                maxLength={150}
                autoComplete="off"
                required
              />
            </div>

            <div className="servicos-form-price-toggle">
              <label className="servicos-form-checkbox-label" htmlFor="usarPrecoPersonalizado">
                <input
                  type="checkbox"
                  id="usarPrecoPersonalizado"
                  checked={usarPrecoPersonalizado}
                  onChange={(e) =>
                    handlePrecoPersonalizadoToggle(e.target.checked)
                  }
                />
                <span className="servicos-form-custom-checkbox">
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path d="m4 10 4 4 8-8" />
                  </svg>
                </span>
                <span className="servicos-form-checkbox-copy">
                  <strong>Usar preço personalizado</strong>
                  <small>Exiba um texto no lugar de um preço fixo.</small>
                </span>
              </label>
            </div>

            {!usarPrecoPersonalizado ? (
              <div className="servicos-form-field">
                <label htmlFor="servico-preco">Preço (R$) *</label>
                <div className="servicos-form-input-prefix">
                  <span>R$</span>
                  <input
                    id="servico-preco"
                    type="number"
                    min="0"
                    step="0.01"
                    value={preco}
                    onChange={(e) => setPreco(e.target.value)}
                    placeholder="0,00"
                    required
                  />
                </div>
                <small className="servicos-form-help">
                  Informe o valor cobrado pelo serviço.
                </small>
              </div>
            ) : (
              <div className="servicos-form-field">
                <label htmlFor="servico-preco-personalizado">
                  Texto do preço personalizado *
                </label>
                <input
                  id="servico-preco-personalizado"
                  type="text"
                  value={precoPersonalizado}
                  onChange={(e) => setPrecoPersonalizado(e.target.value)}
                  placeholder="Ex.: A definir ou Sob consulta"
                  maxLength={100}
                  required
                />
                <div className="servicos-form-info">
                  <span className="servicos-form-info-icon">i</span>
                  <p>
                    Para serviços com preço personalizado, o valor enviado ao
                    sistema continuará sendo zero, conforme a configuração atual.
                  </p>
                </div>
              </div>
            )}

            <div className="servicos-form-field">
              <label htmlFor="servico-duracao">Duração (minutos) *</label>
              <div className="servicos-form-input-suffix">
                <input
                  id="servico-duracao"
                  type="number"
                  min="1"
                  step="1"
                  value={duracao}
                  onChange={(e) => setDuracao(e.target.value)}
                  placeholder="Ex.: 30"
                  required
                />
                <span>min</span>
              </div>
            </div>

            <div className="servicos-form-field">
              <label htmlFor="servico-descricao">Descrição</label>
              <textarea
                id="servico-descricao"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                rows={4}
                maxLength={1000}
                placeholder="Descreva os detalhes do serviço (opcional)"
              />
              <div className="servicos-form-character-count">
                {descricao.length}/1000 caracteres
              </div>
            </div>
          </section>

          <div className="servicos-form-divider" />

          <section className="servicos-form-section">
            <div className="servicos-form-section-heading">
              <span className="servicos-form-section-number">02</span>
              <div>
                <h2>Profissionais disponíveis</h2>
                <p>
                  Escolha quem poderá realizar este serviço.
                </p>
              </div>
            </div>

            <div className="servicos-form-professionals">
              {carregandoProfissionais ? (
                <div className="servicos-form-professionals-state">
                  <span className="servicos-form-spinner" />
                  <span>Carregando profissionais...</span>
                </div>
              ) : profissionais.length > 0 ? (
                <div className="servicos-form-professionals-list">
                  {profissionais.map((profissional) => {
                    const selecionado = profissionaisSelecionados.includes(
                      String(profissional.id)
                    );

                    return (
                      <label
                        key={profissional.id}
                        className={`servicos-form-professional ${
                          selecionado ? "is-selected" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selecionado}
                          onChange={() =>
                            handleProfissionalChange(profissional.id)
                          }
                        />
                        <span className="servicos-form-professional-check">
                          <svg viewBox="0 0 20 20" aria-hidden="true">
                            <path d="m4 10 4 4 8-8" />
                          </svg>
                        </span>
                        <span className="servicos-form-professional-avatar">
                          {(profissional.nome || "?")
                            .trim()
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                        <span className="servicos-form-professional-name">
                          {profissional.nome}
                        </span>
                      </label>
                    );
                  })}
                </div>
              ) : (
                <div className="servicos-form-empty-professionals">
                  <span>i</span>
                  <p>Nenhum profissional cadastrado.</p>
                </div>
              )}
            </div>

            <div className="servicos-form-selection-summary">
              <span className="servicos-form-selection-dot" />
              {profissionaisSelecionados.length === 0
                ? "Nenhum profissional selecionado: o serviço ficará disponível para todos."
                : `${profissionaisSelecionados.length} profissional(is) selecionado(s).`}
            </div>
          </section>

          <footer className="servicos-form-actions">
            <button
              type="button"
              className="servicos-form-btn servicos-form-btn-secondary"
              onClick={() => {
                if (onClose) {
                  onClose();
                } else {
                  navigate("/dashboard");
                }
              }}
              disabled={loading}
            >
              Voltar
            </button>

            <button
              type="submit"
              className="servicos-form-btn servicos-form-btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="servicos-form-button-spinner" />
                  Salvando...
                </>
              ) : (
                <>
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                  {isEdit ? "Salvar alterações" : "Cadastrar serviço"}
                </>
              )}
            </button>
          </footer>
        </form>
      </section>

      <p className="servicos-form-page-note">
        Gerencie seus serviços de forma simples com o Cronos Agenda.
      </p>
    </main>
  );
};

export default ServicosForm;